import I18n from 'I18n';
import * as DSAssetFamilies from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { TASK_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { DEFERRED, IN_PROGRESS, WAITING } from 'customer-data-objects/engagement/EngagementStatusTypes';
import PropertyOptionRecord from 'customer-data-objects/property/PropertyOptionRecord';
import { ENUMERATION } from 'customer-data-objects/property/PropertyTypes';
import { COMPLETION_DATE, CONTACT_TIMEZONE, CREATED_BY, CREATED_DATE, DUE_DATE, ENGAGEMENT_SOURCE, ENGAGEMENT_SOURCE_ID, LAST_ENGAGEMENT, LAST_TOUCH, OWNER_ASSIGNED_DATE, OWNER_ID, PRIORITY, QUEUE_IDS, REPEAT_STATUS, SEQUENCE_STEP_ORDER, STATUS, TYPE } from 'customer-data-objects/task/TaskPropertyNames';
import { List, OrderedMap, OrderedSet, fromJS } from 'immutable';
import { SEQUENCE, TASK
// @ts-expect-error file not typed
} from 'reference-resolvers/constants/ReferenceObjectTypes';
import { always } from 'transmute/__index__';
import getIn from 'transmute/getIn';
import { deepFreeze } from 'usage-tracker-core/common/helpers';
import { getSalesViewsOperatorsForType } from '../../converters/crmSearch/CRMSearchTypeToOperator';
import { getCRMSearchFieldDefinitionsForFilterFamily } from '../../converters/crmSearch/fieldDefinitions/CRMSearchFieldDefinitions';
import { StringDisplayType } from '../../filterQueryFormat/DisplayTypes';
import { SalesViews } from '../../filterQueryFormat/strategy/StrategyTypes';
import { NONE } from '../../filterQueryFormat/timestamps/TimestampTypes';
import { hasLegacyBoardAccess } from '../../permissions/gates';
import { MissingField } from '../../public/core';
import { serializer as CRMSearchSerializer, CRMSearchStrategyWithoutPlaceholders, getField, getFields, getFilterFamilyGroupHeading, getFilterFamilyName, getFilterFamilyOptions, getIsRollingDateOffsetInputEnabled, getReferenceResolvers, getSpecialFieldOptions, onEditorMount, onMount, validateConditionForCRMSearch } from '../CRMSearchStrategy';
import { getFilterFamilyObjectName, getObjectAssociationOptions } from '../strategyUtils';
import * as Operators from 'customer-data-filters-ui/filterQueryFormat/operator/Operators';
// @ts-expect-error file not typed
import PropertyGroupRecord from 'customer-data-objects/property/PropertyGroupRecord';
import { unwrapSingleCRMFilter, wrapSingleCRMFilter } from '../crmSearchStrategyUtils';
const getTaskSourceOptions = () => {
  return List([PropertyOptionRecord({
    // https://hubspot.slack.com/archives/CPMTBGSAG/p1646239307030469
    // As of fall 2021, tasks created from workflows should all have AUTOMATION_PLATFORM for this value.
    // The WORKFLOWS value was used previously but should no longer be getting set by workflows
    value: 'AUTOMATION_PLATFORM',
    label: I18n.text('customerDataFiltersUI.customerDataFilters.FilterEditor.specialOptionValues.tasks.hs_engagement_source.options.AUTOMATION_PLATFORM')
  }), PropertyOptionRecord({
    value: 'SEQUENCES',
    label: I18n.text('customerDataFiltersUI.customerDataFilters.FilterEditor.specialOptionValues.tasks.hs_engagement_source.options.SEQUENCES')
  }), PropertyOptionRecord({
    value: 'SALESFORCE',
    label: I18n.text('customerDataFiltersUI.customerDataFilters.FilterEditor.specialOptionValues.tasks.hs_engagement_source.options.SALESFORCE')
  })]);
};
const TaskVisibleFilterProperties = {
  hs_engagement_source: true,
  hs_engagement_source_id: true,
  hs_repeat_status: true,
  hs_task_sequence_step_order: true
};
const getTaskFields = (filterFamily, props, includeAssociations = false) => {
  var _props$data;
  // Modify hidden to true for some fields
  const allFields = (_props$data = props.data) === null || _props$data === void 0 ? void 0 : _props$data.get(TASK_TYPE_ID);
  let newProps = props;
  if (allFields) {
    var _props$data2;
    const allFieldsWithHiddenModifications = allFields.map(field => {
      if (field.get('name') in TaskVisibleFilterProperties) {
        return field.set('hidden', false).setIn(['wrappedObject', 'hidden'], false);
      }
      return field;
    });
    newProps = Object.assign({}, props, {
      data: (_props$data2 = props.data) === null || _props$data2 === void 0 ? void 0 : _props$data2.set(TASK_TYPE_ID, allFieldsWithHiddenModifications)
    });
  }
  const fields = getFields(filterFamily, newProps, includeAssociations);
  return fields.map((field, fieldId) => {
    switch (fieldId) {
      case ENGAGEMENT_SOURCE:
        return field.set('type', ENUMERATION).set('options', getTaskSourceOptions());
      case ENGAGEMENT_SOURCE_ID:
        return field.set('type', ENUMERATION);
      case STATUS:
        // When user does not have access to legacy board, we can remove legacy statuses from the options
        if (!hasLegacyBoardAccess()) {
          return field.set('options', field.get('options').filter(option => {
            return ![DEFERRED, IN_PROGRESS, WAITING].includes(option.value);
          }));
        }
        return field;
      default:
        return field;
    }
  });
};
const getTaskField = (filterFamily, id, defaultField, props) => {
  const fields = getTaskFields(filterFamily, props, true);
  return getIn([id], fields) || defaultField;
};
const getTaskFieldGroups = (filterFamily, props) => {
  const {
    data
  } = props;
  const allFields = getTaskFields(filterFamily, props, false);
  const groups = ((data === null || data === void 0 ? void 0 : data.getIn([DSAssetFamilies.PROPERTY_GROUP, filterFamily])) || OrderedMap()).filter(group => group.name);
  const groupedFields = allFields.groupBy(field => field.groupName);
  return groups.map((group, groupName) => PropertyGroupRecord({
    displayOrder: group.displayOrder,
    displayName: group.label || group.name,
    name: group.name,
    properties: (groupedFields.get(groupName) || OrderedMap()).toList()
  }));
};
const getTasksFieldGroups = (filterFamily, props) => {
  const originalFieldsGroups = getTaskFieldGroups(filterFamily, props);
  if (filterFamily !== TASK_TYPE_ID) {
    return originalFieldsGroups;
  }
  const mostUsedProperties = [STATUS, PRIORITY, TYPE, DUE_DATE].map(propertyName => getField(filterFamily, propertyName, MissingField({
    name: propertyName,
    timestampType: NONE,
    type: StringDisplayType
  }), props));
  const mostUsedPropertiesFieldGroup = PropertyGroupRecord({
    displayName: I18n.text('filterSidebar.mostUsedProperties'),
    displayOrder: -1,
    name: 'mostUsedProperties',
    properties: List(mostUsedProperties)
  });
  if (originalFieldsGroups.size > 0) {
    const taskFieldGroups = OrderedMap(Object.assign({
      mostUsedProperties: mostUsedPropertiesFieldGroup
    }, originalFieldsGroups === null || originalFieldsGroups === void 0 ? void 0 : originalFieldsGroups.toObject()));
    return taskFieldGroups;
  }
  return originalFieldsGroups;
};
const getTasksFieldDefinitions = filterFamily => {
  const originalFieldDefinitions = getCRMSearchFieldDefinitionsForFilterFamily(filterFamily);
  if (filterFamily !== TASK_TYPE_ID) {
    return originalFieldDefinitions;
  }
  return fromJS(Object.assign({}, originalFieldDefinitions === null || originalFieldDefinitions === void 0 ? void 0 : originalFieldDefinitions.toObject(), {
    [COMPLETION_DATE]: {
      operators: OrderedSet.of(Operators.LessOrEqual, Operators.GreaterOrEqual, Operators.LessRolling, Operators.GreaterRolling, Operators.InRollingDateRange, Operators.InRange, Operators.Known, Operators.NotKnown)
    },
    [CONTACT_TIMEZONE]: {
      inputType: ENUMERATION,
      operators: OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown)
    },
    [CREATED_BY]: {
      operators: OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown)
    },
    [CREATED_DATE]: {
      operators: OrderedSet.of(Operators.LessOrEqual, Operators.GreaterOrEqual, Operators.LessRolling, Operators.GreaterRolling, Operators.InRollingDateRange, Operators.InRange, Operators.Known, Operators.NotKnown)
    },
    [DUE_DATE]: {
      operators: OrderedSet.of(Operators.BeforeNow, Operators.LessOrEqual, Operators.GreaterOrEqual, Operators.LessRolling, Operators.GreaterRolling, Operators.InRollingDateRange, Operators.InRange, Operators.Known, Operators.NotKnown)
    },
    [ENGAGEMENT_SOURCE]: {
      inputType: ENUMERATION,
      operators: OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown)
    },
    [ENGAGEMENT_SOURCE_ID]: {
      referencedObjectType: SEQUENCE,
      inputType: ENUMERATION,
      operators: OrderedSet.of(Operators.In, Operators.NotIn),
      getLabelString: () => I18n.text('customerDataFilters.specialFields.TASKS.hs_engagement_source_id.label'),
      getDescriptionString: () => I18n.text('customerDataFilters.specialFields.TASKS.hs_engagement_source_id.description')
    },
    [LAST_ENGAGEMENT]: {
      operators: OrderedSet.of(Operators.LessOrEqual, Operators.GreaterOrEqual, Operators.LessRolling, Operators.GreaterRolling, Operators.InRollingDateRange, Operators.InRange, Operators.Known, Operators.NotKnown)
    },
    [LAST_TOUCH]: {
      operators: OrderedSet.of(Operators.LessOrEqual, Operators.GreaterOrEqual, Operators.LessRolling, Operators.GreaterRolling, Operators.InRollingDateRange, Operators.InRange, Operators.Known, Operators.NotKnown)
    },
    [OWNER_ASSIGNED_DATE]: {
      operators: OrderedSet.of(Operators.LessOrEqual, Operators.GreaterOrEqual, Operators.LessRolling, Operators.GreaterRolling, Operators.InRollingDateRange, Operators.InRange, Operators.Known, Operators.NotKnown)
    },
    [OWNER_ID]: {
      operators: OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown)
    },
    [PRIORITY]: {
      operators: OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown)
    },
    [QUEUE_IDS]: {
      referencedObjectType: TASK,
      operators: OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown)
    },
    [REPEAT_STATUS]: {
      inputType: ENUMERATION,
      operators: OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown)
    },
    [SEQUENCE_STEP_ORDER]: {
      operators: OrderedSet.of(Operators.Equal, Operators.LessOrEqual, Operators.GreaterOrEqual, Operators.InRange, Operators.Known, Operators.NotKnown)
    },
    [STATUS]: {
      operators: OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown)
    },
    [TYPE]: {
      operators: OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown)
    }
  }));
};
export class TasksSalesViewsStrategy extends CRMSearchStrategyWithoutPlaceholders {
  configure(customConfig) {
    const newStrategy = new TasksSalesViewsStrategy(this.strategyDefinition);
    newStrategy.applyCustomConfig(customConfig);
    return deepFreeze(newStrategy);
  }
}
export const TasksSalesViewsStrategyInstance = deepFreeze(new TasksSalesViewsStrategy({
  onMount,
  onEditorMount,
  getField: getTaskField,
  getFieldDefinitions: getTasksFieldDefinitions,
  getFieldGroups: getTasksFieldGroups,
  getFields: getTaskFields,
  getFilterFamilyOptions,
  getFilterFamilyName,
  getFilterFamilyObjectName,
  getFilterFamilyGroupHeading,
  getObjectAssociationOptions,
  getOperatorsForType: getSalesViewsOperatorsForType,
  getReferenceResolvers,
  getSpecialFieldOptions,
  serializer: CRMSearchSerializer,
  wrapSingleFilter: wrapSingleCRMFilter,
  unwrapSingleFilter: unwrapSingleCRMFilter,
  strategyType: SalesViews,
  validateOperator: validateConditionForCRMSearch,
  getIsFiscalYearEnabled: always(true),
  getIsRollingDateOffsetInputEnabled
}));